/**
 * Revision History modal — create, view, rename, restore, and delete snapshots.
 *
 * CB-7: rides ui/collection-browser.js for the frame (rows, search — gained —
 * skeleton, empty state, selection + floating bulk bar, teardown). This file
 * keeps what is genuinely the surface's: the defer-then-commit delete
 * (`runWithUndo`, no confirm — the record survives the undo window), the
 * restore flow that locks EVERY row before its confirm opens, the inline
 * rename (protected by the module's `state.editing` contract so a concurrent
 * refresh cannot destroy typed text), the create-snapshot inline input, Clear
 * All (which keeps its confirm — it is not undoable), and the exact
 * `.pb-rev-*` row markup the stylesheet and revision-defer-tests address.
 *
 * DECISION (phase entry statement): bulk delete uses ONE keyed undo for the
 * whole batch — the same defer-then-commit semantics as the single path, one
 * toast, one Undo restoring everything. Neither path shows a confirm; two
 * deletion semantics on one surface is a bug (plan CB-7).
 *
 * Escape-in-rename FIX (CB-2 finding): the rename and create inputs call
 * `stopPropagation()` on Escape, so abandoning an edit no longer reaches the
 * focus trap and closes the whole panel.
 */

import { eventBus } from '../core/events.js';
import { showConfirmModal, showToast } from './shell.js';
import { runWithUndo } from './deferred-ops.js';
import { store } from '../core/state.js';
import { isLoadSupersededError } from '../core/save-load.js';
import { reportError } from '../core/report-error.js';
import { createButton, isButtonBusy, setButtonBusy } from './components.js';
import { openCollectionBrowser } from './collection-browser.js';

function formatRelativeTime(timestamp) {
  const diff = Date.now() - timestamp;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function kindBadge(rev) {
  if (rev.kind === 'checkpoint')
    return { className: 'pb-rev-badge-checkpoint', label: 'Checkpoint' };
  if (rev.trigger === 'auto') return { className: 'pb-rev-badge-auto', label: 'Auto' };
  return { className: 'pb-rev-badge-manual', label: 'Manual' };
}

export function showRevisionPanel(revisionStore) {
  let api = null;
  let totals = { count: 0, size: 0 };
  let footerEl = null;

  /** The row body + trail — DOM-first, exactly the pre-migration classes. */
  function buildRowContent(rev) {
    const frag = document.createDocumentFragment();

    const info = document.createElement('div');
    info.className = 'pb-rev-info';
    const name = document.createElement('span');
    name.className = 'pb-rev-name';
    name.dataset.revId = rev.id;
    name.setAttribute('role', 'button');
    name.tabIndex = 0;
    name.setAttribute('aria-label', `Rename snapshot ${rev.name}`);
    name.dataset.tooltip = 'Double-click, Enter, or F2 to rename';
    name.textContent = rev.name;
    const meta = document.createElement('span');
    meta.className = 'pb-rev-meta';
    const time = document.createElement('span');
    time.className = 'pb-rev-time';
    time.textContent = formatRelativeTime(rev.timestamp);
    const size = document.createElement('span');
    size.className = 'pb-rev-size';
    size.textContent = formatSize(rev.stateSize || 0);
    const badge = kindBadge(rev);
    const badgeEl = document.createElement('span');
    badgeEl.className = `pb-rev-badge ${badge.className}`;
    badgeEl.textContent = badge.label;
    meta.append(time, size, badgeEl);
    info.append(name, meta);

    const actions = document.createElement('div');
    actions.className = 'pb-rev-actions';
    actions.append(
      createButton({
        variant: 'outline',
        size: 'sm',
        label: 'Restore',
        className: 'pb-rev-restore',
        dataset: { revId: rev.id }
      }),
      createButton({
        variant: 'outline',
        size: 'sm',
        content: [document.createTextNode('×')],
        ariaLabel: `Delete snapshot ${rev.name}`,
        tooltip: 'Delete snapshot',
        className: 'pb-rev-delete',
        dataset: { revId: rev.id }
      })
    );

    frag.append(info, actions);
    return frag;
  }

  function setRestoreBusy(busy) {
    for (const btn of api.listEl.querySelectorAll('.pb-rev-restore')) {
      btn.disabled = busy;
      btn.setAttribute('aria-busy', 'false');
    }
  }

  async function restoreRevision(btn) {
    if (btn.disabled) return;
    if (!store.isWritable()) {
      showToast('This tab is read-only — another tab is editing this project.', 'info');
      return;
    }
    const id = btn.dataset.revId;
    // Lock every row BEFORE the dialog opens, not after it resolves — the old
    // order let a second row open its own confirm, racing two whole-project
    // restores.
    setRestoreBusy(true);
    try {
      let superseded = null;
      const restored = await showConfirmModal({
        title: 'Restore Snapshot',
        message: 'This will replace your current project with the selected snapshot.',
        details: 'Your current state will be replaced immediately.',
        confirmLabel: 'Restore',
        confirmBusyLabel: 'Restoring…',
        cancelLabel: 'Cancel',
        danger: true,
        errorScope: 'Revision restore',
        onConfirm: async () => {
          try {
            await revisionStore.restore(id, { requireWritable: true });
          } catch (err) {
            if (isLoadSupersededError(err)) {
              superseded = err;
              return;
            }
            throw new Error('Failed to restore: ' + err.message, { cause: err });
          }
        }
      });
      if (!restored) return;
      if (superseded) {
        showToast(superseded.message, 'info');
        return;
      }
      eventBus.emit('preview:render-request', { preserveScroll: false });
      api.close();
    } finally {
      if (!api.isClosed()) setRestoreBusy(false);
    }
  }

  /**
   * Defer-then-commit delete, single or batch: rows leave optimistically and
   * the actual `revisionStore.delete()` calls are held behind ONE undo window
   * (one key, one toast, one Undo restoring everything). "Clear All" keeps
   * its confirm — it is not undoable.
   */
  async function deleteRevisions(ids) {
    if (!ids.length) return;
    api.removeRecords(ids);
    try {
      const outcome = await runWithUndo({
        key: `undo-delete-revision-${ids.join('-')}`,
        message: ids.length === 1 ? 'Snapshot deleted' : `${ids.length} snapshots deleted`,
        run: async () => {
          for (const id of ids) await revisionStore.delete(id);
        },
        onUndo: () => api.refresh()
      });
      await api.refresh();
      if (outcome === 'undone') focusPrimaryRevisionControl();
    } catch (err) {
      reportError('Revision history', err, {
        user: 'toast',
        message: `Snapshot delete failed: ${err?.message || err}`,
        id: `revision-delete-failed-${ids[0]}`
      });
      try {
        await api.refresh();
        focusPrimaryRevisionControl();
      } catch (refreshErr) {
        reportError('Revision history refresh', refreshErr);
      }
    }
  }

  function focusPrimaryRevisionControl() {
    const first = api.body.querySelector(
      '.pb-rev-restore, .pb-rev-item .pb-rev-name, #pb-rev-create'
    );
    if (first && typeof first.focus === 'function') first.focus();
  }

  function startRename(span) {
    const id = span.dataset.revId;
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'pb-input pb-rev-rename-input';
    input.maxLength = 80;
    input.value = span.textContent;
    span.replaceWith(input);
    // The module's editing contract: this row is now carried whole across any
    // rebuild — a concurrent refresh cannot destroy the typed value.
    api.setItemEditing(id, true);
    input.focus();
    input.select();

    const commit = async () => {
      const newName = input.value.trim() || 'Unnamed';
      api.setItemEditing(id, false);
      try {
        await revisionStore.rename(id, newName);
      } catch (err) {
        reportError('Revision history', err, {
          user: 'toast',
          message: 'Could not rename the snapshot',
          id: `revision-rename-failed-${id}`
        });
      }
      api.refresh();
    };
    input.addEventListener('blur', commit);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        input.blur();
      }
      if (e.key === 'Escape') {
        // THE CB-2 FINDING, FIXED: without stopPropagation this Escape reaches
        // the focus trap and abandoning a rename closes the whole panel.
        e.preventDefault();
        e.stopPropagation();
        input.removeEventListener('blur', commit);
        api.setItemEditing(id, false);
        api.refresh().then(() => focusPrimaryRevisionControl());
      }
    });
  }

  function startCreateSnapshot() {
    if (api.listEl.querySelector('.pb-rev-create-input')) return;
    const row = document.createElement('div');
    row.className = 'pb-rev-item';
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'pb-input pb-rev-create-input';
    input.value = 'Manual snapshot';
    input.placeholder = 'Snapshot name';
    input.style.flex = '1';
    row.appendChild(input);
    api.listEl.prepend(row);
    input.focus();
    input.select();
    const commit = async () => {
      const name = input.value.trim() || 'Manual snapshot';
      row.remove();
      await revisionStore.save(name, '', 'manual');
      api.refresh().then(() => focusPrimaryRevisionControl());
    };
    const cancel = () => row.remove();
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commit();
      }
      if (e.key === 'Escape') {
        // Same trap-escape hazard as the rename input; same fix.
        e.preventDefault();
        e.stopPropagation();
        cancel();
      }
    });
    input.addEventListener('blur', () => {
      if (row.parentNode) commit();
    });
  }

  function paintFooter() {
    if (!footerEl) return;
    footerEl.textContent = '';
    if (totals.count === 0) return;
    const total = document.createElement('span');
    total.className = 'pb-rev-total';
    total.textContent = `${totals.count} snapshots · ${formatSize(totals.size)}`;
    const clearBtn = createButton({
      variant: 'outline',
      size: 'sm',
      label: 'Clear All',
      className: 'pb-rev-clear-all',
      id: 'pb-rev-clear-all',
      onClick: async () => {
        if (isButtonBusy(clearBtn)) return;
        setButtonBusy(clearBtn, true, { busyLabel: null });
        try {
          const cleared = await showConfirmModal({
            title: 'Clear Revision History',
            message: "Delete all of this project's snapshots?",
            details:
              'Checkpoints included. Other projects and legacy records are untouched. This cannot be undone.',
            confirmLabel: 'Delete All',
            confirmBusyLabel: 'Deleting…',
            cancelLabel: 'Cancel',
            danger: true,
            errorScope: 'Revision clear-all',
            onConfirm: () => revisionStore.clearAll()
          });
          if (!cleared) return;
          showToast('All snapshots deleted', 'success');
          api.refresh().then(() => focusPrimaryRevisionControl());
        } finally {
          if (clearBtn.isConnected) setButtonBusy(clearBtn, false);
        }
      }
    });
    footerEl.append(total, clearBtn);
  }

  api = openCollectionBrowser({
    title: 'Manage snapshots',
    initialFocus: 'search', // PP-2: search owns focus at time zero, through the kit
    testid: 'revision-history',
    size: 'md',
    overlayClassName: 'pb-revision-overlay',
    className: 'pb-modal-scrollable',
    layout: 'rows',
    listTag: 'div',
    itemTag: 'div',
    listAriaLabel: 'Snapshots',
    search: { placeholder: 'Search snapshots…', ariaLabel: 'Search snapshots' },
    chips: false,
    skeleton: { rowVariant: 'revision', count: 4 },
    emptyCopy: {
      title: 'No snapshots yet',
      body:
        'A snapshot is a named copy of the whole project you can restore later — ' +
        'useful before a big change. One is also taken automatically before a load, ' +
        'an export, or Start Over.'
    },
    filteredEmptyCopy: { title: 'No snapshots match this search.' },
    errorCopy: { title: 'Could not load snapshots.' },

    load: async () => {
      const revisions = await revisionStore.list();
      // Quarantined v1 records with no derivable project: kept visible under an
      // explicit legacy group — never silently hidden, never silently deleted.
      let unassigned = [];
      try {
        unassigned = await revisionStore.list({ unassigned: true });
      } catch {
        /* expected-flow fallback: legacy listing is best-effort decoration */
      }
      totals = {
        count: revisions.length,
        size: revisions.reduce((sum, r) => sum + (r.stateSize || 0), 0)
      };
      return [
        ...revisions.map((rev) => ({ ...rev })),
        ...unassigned.map((rev) => ({ ...rev, _unassigned: true }))
      ];
    },

    adapt: (rev) => ({
      id: String(rev.id),
      title: rev.name,
      searchText: String(rev.name || '').toLowerCase(),
      className: 'pb-rev-item',
      content: buildRowContent(rev),
      groupKey: rev._unassigned ? 'legacy' : 'scoped',
      data: rev
    }),

    selection: {
      noun: 'snapshot',
      bulkIntents: [
        {
          id: 'delete-selected',
          label: 'Delete selected',
          variant: 'danger',
          enabled: (items) => items.length > 0,
          perform: (items) => deleteRevisions(items.map((item) => item.id))
        }
      ]
    },

    onOpen(handle) {
      handle.listEl.id = 'pb-rev-list';
      handle.listEl.classList.add('pb-rev-list');
      const scroller = handle.body.querySelector('.pb-coll-scroll');
      const shell = handle.body.querySelector('.pb-coll-shell');
      shell?.insertBefore(
        createButton({
          variant: 'outline',
          size: 'sm',
          label: 'Create Snapshot',
          className: 'pb-rev-create-btn',
          id: 'pb-rev-create',
          onClick: () => startCreateSnapshot()
        }),
        shell.firstChild
      );
      footerEl = document.createElement('div');
      footerEl.className = 'pb-rev-footer';
      footerEl.id = 'pb-rev-footer';
      shell?.appendChild(footerEl);
      if (scroller) scroller.after(footerEl);

      // Delegated once, not per-render: restore, delete, rename.
      handle.listEl.addEventListener('click', (event) => {
        const restoreBtn = event.target.closest('.pb-rev-restore');
        if (restoreBtn) {
          void restoreRevision(restoreBtn);
          return;
        }
        const deleteBtn = event.target.closest('.pb-rev-delete');
        if (deleteBtn) void deleteRevisions([deleteBtn.dataset.revId]);
      });
      handle.listEl.addEventListener('dblclick', (event) => {
        const span = event.target.closest('.pb-rev-name');
        if (span) startRename(span);
      });
      handle.listEl.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== 'F2') return;
        const span = event.target.closest('.pb-rev-name');
        if (!span) return;
        event.preventDefault();
        startRename(span);
      });
    },

    onRender(handle) {
      // The suite and stylesheet address rows by data-rev-id; groups get their
      // legacy label re-inserted as a FOREIGN sibling after every rebuild.
      let firstLegacy = null;
      for (const item of handle.getItems()) {
        const el = handle.itemEl(item.id);
        if (el) el.dataset.revId = item.id;
        if (!firstLegacy && item.groupKey === 'legacy') firstLegacy = handle.itemEl(item.id);
      }
      if (firstLegacy && !handle.listEl.querySelector('.pb-rev-group-label')) {
        const label = document.createElement('div');
        label.className = 'pb-rev-group-label';
        label.textContent = 'Unassigned (legacy)';
        const note = document.createElement('p');
        note.className = 'pb-rev-group-note';
        note.textContent =
          'Snapshots from before project scoping whose project could not be determined. ' +
          'They restore and delete normally.';
        firstLegacy.before(label, note);
      }
      // The rich empty state keeps its call to action AND its .pb-rev-empty
      // identity — layout-stability-tests addresses it by the old class.
      const emptyEl = handle.body.querySelector('.pb-coll-empty');
      emptyEl?.classList.add('pb-rev-empty');
      if (emptyEl && !emptyEl.hidden && totals.count === 0 && !emptyEl.querySelector('button')) {
        emptyEl.appendChild(
          createButton({
            variant: 'primary',
            size: 'sm',
            label: 'Create your first snapshot',
            className: 'pb-rev-empty-create',
            onClick: () => startCreateSnapshot()
          })
        );
      }
      paintFooter();
    }
  });

  if (!api) return;
  focusPrimaryRevisionControl();
}
